"use client";

/**
 * Memory data layer — what is left of it after the Journal retired (Ruling 281).
 *
 * TWO HOOKS, AND EACH HAS ONE CALLER. The profile read stays because
 * `KnowledgeWarmup` pre-warms it app-wide, and the import mutation stays because
 * the import wizard is still reached from the Brain's crumb bar and from
 * arrival. Everything else here served the Journal room — the situation feed,
 * the dream probe, correct, forget, the diary-line edit — and retired with it.
 *
 * THE BRAIN'S OWN WRITES ARE NOT HERE. They deposit and forget over the same
 * `/api/memory/*` plane, but they invalidate the BRAIN's caches, which is what
 * the reader is looking at; they live in `components/brain/brainAnswerBack.ts`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { resilientFetch } from "@/lib/fetch";
import type { MemoryEntry } from "@bash/shared";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

/**
 * WHAT THE ROUTE SENDS, and every field is still on the wire — the warmup holds
 * the whole payload, and the Journal's room is what stopped reading most of it.
 * `rooms` is the fold's own section names and had a type of its own; nothing
 * types it now, so it rides as the shape the route sends rather than as a
 * definition kept alive for a reader that no longer exists.
 */
export interface MemoryProfileData {
  profile?: { markdown?: string; updatedAt?: string };
  observations?: MemoryEntry[];
  rooms?: Array<{ id: string; name: string; synthesisMd: string }>;
  total?: number;
}

function useAuthHeaders() {
  const { getAccessToken } = useAuth();
  return async (jsonBody = false): Promise<Record<string, string>> => {
    const token = await getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (jsonBody) headers["Content-Type"] = "application/json";
    return headers;
  };
}

export const memoryKeys = {
  profile: (userId: string | null) => ["memory", "profile", userId] as const,
};

/** Required document: failure here IS an error state (retry UI, never "empty brain"). */
export function useMemoryProfile() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<MemoryProfileData>({
    queryKey: memoryKeys.profile(userId),
    queryFn: async () => {
      const res = await resilientFetch(`${SERVER_URL}/api/memory`, {
        headers: await getHeaders(),
      });
      if (!res.ok) throw new Error(`memory ${res.status}`);
      return (await res.json()) as MemoryProfileData;
    },
    enabled: !!userId,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
}

export interface ImportMemoriesResult {
  imported: number;
  duplicates: number;
  total: number;
}

export interface ImportMemoriesPayload {
  platform: string;
  items: Array<{ text: string; epistemic?: string; observedAt?: string }>;
}

/**
 * Bulk import from another platform (the import wizard's confirm step). The
 * server deposits onto the observations rail and drains folds in the
 * background — the settled refetch shows the new rows in All memories at once.
 */
export function useImportMemories() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const getHeaders = useAuthHeaders();
  return useMutation<ImportMemoriesResult, Error, ImportMemoriesPayload>({
    mutationFn: async (payload) => {
      const res = await resilientFetch(`${SERVER_URL}/api/memory/import`, {
        method: "POST",
        headers: await getHeaders(true),
        body: JSON.stringify(payload),
        timeout: 60_000,
      });
      if (!res.ok) throw new Error(`import ${res.status}`);
      return (await res.json()) as ImportMemoriesResult;
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: memoryKeys.profile(userId) }),
  });
}
